import { useMemo } from "react";
import { Box, Button, Theme, Tooltip, Typography, alpha } from "@mui/material";
import ErrorIcon from "@mui/icons-material/Error";
import { IVacationAwardingSlotUsage, IVacationBidSlot } from "@vmosolutions/oc-interfaces";

import { VACATION_BID_TEST_ID } from "../testIds";
import { displaySlotId } from "../helpers";
import { fillBand, overCapacity, slotDateRange, slotsByMonth } from "./awardingHelpers";
import { fillBandColor } from "./RankedBidList";

export interface IBlockCapacityGridProps {
  slots: IVacationBidSlot[];
  usageBySlot: Map<string, IVacationAwardingSlotUsage>;
  crewNamesBySlot: Map<string, string[]>;
  showSlotIdYear: boolean;
  /** When set, only these slots are shown. */
  onlySlotIds: Set<string> | null;
  onlyToggleLabel: string;
  onlyToggleDisabled: boolean;
  onToggleOnly: () => void;
  /** Called when a block is clicked. Blocks cannot be clicked when absent. */
  onSlotClick?: (slotId: string) => void;
  /** Heading. "Block capacity" when absent. */
  title?: string;
  /** Replaces the hint that follows the heading. */
  hint?: string;
  /** When set, the blocks outside it are greyed out. */
  selectedSlotIds?: Set<string>;
  /** Makes every block a toggle for its selection. Ignored while `onSlotClick` is set. */
  onToggleSlot?: (slotId: string) => void;
  /** Blocks shown greyed out and not clickable, with the reason in their tooltip. */
  locked?: { slotIds: Set<string>; reason: string };
}

const BlockCapacityGridStyles = (theme: Theme): any => ({
  padding: "10px 14px",
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.custom.forms.inputBorder}`,
  borderRadius: "3px",
  "& .grid_header": {
    display: "flex",
    alignItems: "center",
    marginBottom: "8px",
    "& .grid_title": { fontSize: 11, textTransform: "uppercase", color: theme.custom.forms.labels },
    "& .grid_hint": { fontSize: 11, color: theme.custom.forms.labels, marginLeft: "8px", flex: 1 },
    "& .MuiButtonBase-root": { padding: "1px 8px", fontSize: 11, textTransform: "none" },
  },
  "& .grid_blocks": { display: "flex", flexWrap: "wrap", alignItems: "center", gap: "6px" },
  "& .month_label": {
    fontSize: 11,
    color: theme.custom.forms.labels,
    minWidth: "28px",
    textAlign: "right",
  },
  "& .block_chip": {
    // A button when blocks can be clicked, reset to match the plain card.
    font: "inherit",
    color: "inherit",
    textAlign: "left",
    display: "block",
    width: "130px",
    padding: "4px 8px 5px",
    borderRadius: "3px",
    border: `1px solid ${theme.custom.forms.inputBorder}`,
    backgroundColor: theme.custom.timeline.contextListBackground,
    "&.full": { borderColor: theme.palette.error.main },
    "&.deselected": {
      opacity: 0.35,
      filter: "grayscale(1)",
      borderStyle: "dashed",
      backgroundColor: alpha(theme.custom.timeline.contextListBackground, 0.4),
    },
    "&.clickable": {
      cursor: "pointer",
      "&:hover": { borderColor: theme.palette.primary.main },
      "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: "1px" },
    },
    "& .chip_top": { display: "flex", justifyContent: "space-between", fontSize: 12 },
    "& .chip_id": { fontWeight: 700 },
    "& .chip_count": { fontSize: 11, display: "inline-flex", alignItems: "center", gap: "2px" },
    "& .over_icon": { fontSize: 13 },
    "& .chip_bar": {
      display: "block",
      marginTop: "4px",
      height: "3px",
      borderRadius: "2px",
      backgroundColor: theme.custom.forms.inputBorder,
      overflow: "hidden",
      "& > span": { display: "block", height: "100%" },
    },
  },
});

/** Every block's awarded count against its capacity, month by month. */
export const BlockCapacityGrid = (props: IBlockCapacityGridProps): JSX.Element => {
  const {
    slots,
    usageBySlot,
    crewNamesBySlot,
    showSlotIdYear,
    onlySlotIds,
    onlyToggleLabel,
    onlyToggleDisabled,
    onToggleOnly,
    onSlotClick,
    title = "Block capacity",
    hint,
    selectedSlotIds,
    onToggleSlot,
    locked,
  } = props;

  const onChipClick = onSlotClick ?? onToggleSlot;
  const toggles = !onSlotClick && !!onToggleSlot;

  const months = useMemo(
    () => slotsByMonth(onlySlotIds ? slots.filter((z) => onlySlotIds.has(z.slotId)) : slots),
    [slots, onlySlotIds]
  );

  return (
    <Box sx={BlockCapacityGridStyles} data-testid={VACATION_BID_TEST_ID.blockCapacityGrid}>
      <div className="grid_header">
        <Typography className="grid_title">{title}</Typography>
        <Typography className="grid_hint">
          {hint ??
            `${slots.length} blocks · hover a block to see who holds it${onSlotClick ? " · click one to award it" : ""}`}
        </Typography>
        <Button
          size="small"
          variant={onlySlotIds ? "contained" : "outlined"}
          disabled={onlyToggleDisabled}
          onClick={onToggleOnly}
          data-testid={VACATION_BID_TEST_ID.showCurrentBidsToggle}
        >
          {onlyToggleLabel}
        </Button>
      </div>
      <div className="grid_blocks">
        {months.map((month) => [
          <Typography key={`${month.key}-label`} className="month_label">
            {month.label}
          </Typography>,
          ...month.slots.map((slot) => {
            const usage = usageBySlot.get(slot.slotId);
            const awarded = usage?.awarded ?? 0;
            const capacity = usage?.capacity ?? slot.capacity;
            const band = fillBand({ awarded, capacity });
            const over = overCapacity({ awarded, capacity });
            const holders = crewNamesBySlot.get(slot.slotId) ?? [];
            const isLocked = !!locked?.slotIds.has(slot.slotId);
            const selected = !isLocked && (!selectedSlotIds || selectedSlotIds.has(slot.slotId));
            const onClick = isLocked ? undefined : onChipClick;
            return (
              <Tooltip
                key={slot.slotId}
                placement="top"
                title={
                  <>
                    {over > 0 && (
                      <div style={{ fontWeight: 700 }}>
                        {`Over capacity by ${over}: ${awarded} awarded against ${capacity} place${capacity === 1 ? "" : "s"}.`}
                      </div>
                    )}
                    {isLocked && <div style={{ fontWeight: 700 }}>{locked?.reason}</div>}
                    <div>{slotDateRange(slot)}</div>
                    <div>{holders.length ? holders.join(", ") : "Nobody awarded yet"}</div>
                  </>
                }
              >
                <Box
                  component={onClick ? "button" : "div"}
                  type={onClick ? "button" : undefined}
                  className={`block_chip${band === "full" ? " full" : ""}${onClick ? " clickable" : ""}${selected ? "" : " deselected"}`}
                  onClick={onClick ? () => onClick(slot.slotId) : undefined}
                  aria-pressed={toggles && onClick ? selected : undefined}
                  data-testid={VACATION_BID_TEST_ID.blockCapacityChip}
                >
                  <span className="chip_top">
                    <span className="chip_id">{displaySlotId(slot.slotId, showSlotIdYear)}</span>
                    <Box
                      component="span"
                      className="chip_count"
                      sx={(theme) => ({ color: fillBandColor(theme, band) })}
                    >
                      {over > 0 && <ErrorIcon className="over_icon" aria-label="Over capacity" />}
                      {`${awarded}/${capacity}`}
                    </Box>
                  </span>
                  <span className="chip_bar">
                    <Box
                      component="span"
                      sx={(theme) => ({
                        width: `${capacity ? Math.min(100, (awarded / capacity) * 100) : 100}%`,
                        backgroundColor: fillBandColor(theme, band),
                      })}
                    />
                  </span>
                </Box>
              </Tooltip>
            );
          }),
        ])}
      </div>
    </Box>
  );
};

export default BlockCapacityGrid;
